// WorkflowSection.jsx
// "From Idea to Discovery" — a curved, animated flow.
import { useEffect, useRef, useState } from "react";
import {
  Box,
  Container,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import TerminalOutlinedIcon from "@mui/icons-material/TerminalOutlined";
import ScienceOutlinedIcon from "@mui/icons-material/ScienceOutlined";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";

import { GradientTitle } from "../components/GradientTitle";
import { IconDark } from "../components/IconNode";
import {
  ACCENT,
  ACCENT_DARK,
  INK,
  INK_SECONDARY,
  HAIRLINE,
  SURFACE,
  withAlpha,
} from "../../../theme/tokens";

const STEPS = [
  {
    icon: LightbulbOutlinedIcon,
    label: "Hypothesis",
    desc: "Start from a research question or idea.",
  },
  {
    icon: TerminalOutlinedIcon,
    label: "Workspace",
    desc: "Spin up an interactive environment in minutes.",
  },
  {
    icon: ScienceOutlinedIcon,
    label: "Develop & Validate",
    desc: "Build and test your workflow on real resources.",
  },
  {
    icon: RocketLaunchOutlinedIcon,
    label: "Job",
    desc: "Launch at scale from a single definition.",
  },
  {
    icon: AssessmentOutlinedIcon,
    label: "Results",
    desc: "Track metrics, logs, and artifacts in one place.",
  },
  {
    icon: AccountTreeOutlinedIcon,
    label: "Reproduce & Share",
    desc: "Fork, reproduce, and publish your experiment.",
  },
];

// --- Desktop geometry (SVG viewBox is 0..VBW × 0..H; x stretches to width) ---
const N = STEPS.length;
const VBW = 1000;
const H = 400;
const NODE = 60;
const GAP = 16; // node ↔ card gap
const Y_TOP = 150; // even-index node centers (cards above)
const Y_BOT = 250; // odd-index node centers (cards below)
const CARD_W = 168;
const CARD_W_WIDE = 210; // for steps whose title needs one line (3 & 6)

const xPct = (i) => 11 + (i / (N - 1)) * 78; // inset 11%..89%
const xVB = (i) => (xPct(i) / 100) * VBW;
const nodeY = (i) => (i % 2 === 0 ? Y_TOP : Y_BOT);

// Smooth wave through the nodes using horizontal bezier handles (React-Flow
// style), extended a touch past the first/last node so it reads as a rail.
const buildWavePath = () => {
  const pts = STEPS.map((_, i) => ({ x: xVB(i), y: nodeY(i) }));
  let d = `M ${pts[0].x - 70} ${pts[0].y} L ${pts[0].x} ${pts[0].y}`;
  for (let i = 1; i < pts.length; i++) {
    const p0 = pts[i - 1];
    const p1 = pts[i];
    const mx = (p0.x + p1.x) / 2;
    d += ` C ${mx} ${p0.y} ${mx} ${p1.y} ${p1.x} ${p1.y}`;
  }
  const last = pts[pts.length - 1];
  d += ` L ${last.x + 70} ${last.y}`;
  return d;
};

const StepNode = ({ icon, active, size = NODE }) => (
  <Box
    sx={{
      position: "relative",
      flexShrink: 0,
      transition: "transform .2s ease",
      transform: active ? "scale(1.08)" : "none",
    }}
  >
    <Box
      sx={{
        display: "flex",
        borderRadius: "50%",
        transition: "box-shadow .2s ease",
        boxShadow: active ? `0 0 0 6px ${withAlpha(ACCENT, 0.14)}` : "none",
        // IconBase adds an mb:1; cancel it so the circle's true center sits
        // exactly on the connecting line.
        "& > div": { marginBottom: 0 },
      }}
    >
      <IconDark icon={icon} size={size} />
    </Box>
  </Box>
);

const StepCard = ({ index, label, desc, active, cardWidth = CARD_W }) => (
  <Paper
    elevation={0}
    sx={{
      width: { xs: "100%", md: cardWidth },
      minHeight: { md: 120 },
      p: 2,
      borderRadius: 2.5,
      bgcolor: SURFACE,
      border: `1px solid ${active ? withAlpha(ACCENT, 0.5) : HAIRLINE}`,
      boxShadow: active
        ? "0 16px 34px rgba(16,24,40,0.12)"
        : "0 8px 24px rgba(16,24,40,0.06)",
      transform: active ? "translateY(-4px)" : "none",
      transition:
        "transform .2s ease, box-shadow .2s ease, border-color .2s ease",
    }}
  >
    <Typography
      sx={{ fontWeight: 700, fontSize: "0.95rem", color: INK, mb: 0.5 }}
    >
      {index + 1}. {label}
    </Typography>
    <Typography
      sx={{ color: INK_SECONDARY, fontSize: "0.82rem", lineHeight: 1.5 }}
    >
      {desc}
    </Typography>
  </Paper>
);

const DesktopFlow = () => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  const [hover, setHover] = useState(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const ob = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          ob.disconnect();
        }
      },
      { threshold: 0.2 },
    );
    ob.observe(el);
    return () => ob.disconnect();
  }, []);

  const wavePath = buildWavePath();

  // Opacity/transition only — the centering transform is composed per element
  // (combined with the reveal offset) so it is never overwritten.
  const reveal = (i) => ({
    opacity: visible ? 1 : 0,
    transition: "opacity .55s ease, transform .55s ease",
    transitionDelay: `${i * 0.09}s`,
  });

  return (
    <Box ref={ref} sx={{ position: "relative", height: H, width: "100%" }}>
      {/* Curved flow path */}
      <Box
        component="svg"
        viewBox={`0 0 ${VBW} ${H}`}
        preserveAspectRatio="none"
        sx={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          overflow: "visible",
          opacity: visible ? 1 : 0,
          transition: "opacity .8s ease",
        }}
      >
        {/* node → card stubs (dashed) */}
        {STEPS.map((_, i) => {
          const x = xVB(i);
          const y = nodeY(i);
          const y1 = i % 2 === 0 ? y - NODE / 2 : y + NODE / 2;
          const y2 = i % 2 === 0 ? y - NODE / 2 - GAP : y + NODE / 2 + GAP;
          return (
            <line
              key={i}
              x1={x}
              y1={y1}
              x2={x}
              y2={y2}
              stroke={withAlpha(ACCENT, 0.5)}
              strokeWidth={2}
              strokeDasharray="3 4"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          );
        })}

        {/* Flowing particles — small dashes drifting left → right */}
        <Box
          component="path"
          d={wavePath}
          sx={{
            fill: "none",
            stroke: ACCENT,
            strokeWidth: 4,
            strokeLinecap: "round",
            strokeDasharray: "2 20",
            vectorEffect: "non-scaling-stroke",
            "@keyframes flowParticles": { to: { strokeDashoffset: -220 } },
            animation: "flowParticles 6s linear infinite",
          }}
        />
      </Box>

      {/* Nodes */}
      {STEPS.map((s, i) => (
        <Box
          key={`node-${s.label}`}
          onMouseEnter={() => setHover(i)}
          onMouseLeave={() => setHover(null)}
          sx={{
            position: "absolute",
            left: `${xPct(i)}%`,
            top: `${nodeY(i)}px`,
            transform: visible
              ? "translate(-50%, -50%)"
              : "translate(-50%, calc(-50% + 18px))",
            zIndex: 2,
            ...reveal(i),
          }}
        >
          <StepNode icon={s.icon} active={hover === i} />
        </Box>
      ))}

      {/* Cards */}
      {STEPS.map((s, i) => {
        const above = i % 2 === 0;
        return (
          <Box
            key={`card-${s.label}`}
            onMouseEnter={() => setHover(i)}
            onMouseLeave={() => setHover(null)}
            sx={{
              position: "absolute",
              left: `${xPct(i)}%`,
              transform: visible
                ? "translateX(-50%)"
                : "translateX(-50%) translateY(18px)",
              ...(above
                ? { bottom: `${H - (Y_TOP - NODE / 2 - GAP)}px` }
                : { top: `${Y_BOT + NODE / 2 + GAP}px` }),
              zIndex: 1,
              ...reveal(i),
            }}
          >
            <StepCard
              index={i}
              label={s.label}
              desc={s.desc}
              active={hover === i}
              cardWidth={i === 1 || i === 2 || i === 5 ? CARD_W_WIDE : CARD_W}
            />
          </Box>
        );
      })}
    </Box>
  );
};

/* Mobile: a clean vertical timeline. */
const MobileFlow = () => (
  <Stack sx={{ maxWidth: 480, mx: "auto" }}>
    {STEPS.map((s, i) => (
      <Box key={s.label} sx={{ display: "flex", gap: 2 }}>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <StepNode icon={s.icon} size={56} />
          {i < STEPS.length - 1 && (
            <Box
              sx={{
                width: 2,
                flex: 1,
                minHeight: 28,
                my: 0.5,
                borderRadius: 2,
                background: `linear-gradient(${ACCENT}, ${ACCENT_DARK})`,
                opacity: 0.7,
              }}
            />
          )}
        </Box>
        <Box sx={{ pb: 3, flex: 1 }}>
          <StepCard index={i} label={s.label} desc={s.desc} />
        </Box>
      </Box>
    ))}
  </Stack>
);

const WorkflowSection = () => {
  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Box
      id="workflow"
      sx={{
        py: { xs: 8, md: 12 },
        px: { xs: 2, md: 4 },
        backgroundColor: "#fff",
      }}
    >
      <Container maxWidth="lg">
        <GradientTitle>From Idea to Discovery</GradientTitle>

        <Typography
          sx={{
            color: "text.secondary",
            textAlign: "center",
            maxWidth: 760,
            mx: "auto",
            mb: { xs: 5, md: 8 },
            fontSize: { xs: "1rem", md: "1.1rem" },
          }}
        >
          A unified path from hypothesis to scientific results.
        </Typography>

        {isMdDown ? <MobileFlow /> : <DesktopFlow />}
      </Container>
    </Box>
  );
};

export default WorkflowSection;
